import { Button, Card, CardBody, CardHeaderWithTabs } from '@statsbomb/kitbag-components';
import { useAtomValue, useSetAtom } from 'jotai';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { dataLockerLandingPageSelectedTabAtom } from '@/atoms/dataLocker/landingPage';
import { resetAllFiltersAtom } from '@/atoms/filters/resetFilters';
import { playerAggsFilterParamsAtom } from '@/atoms/queries/player/playerAggData';
import { SwitchToClassicPlayerScoutButton } from '@/components/button/SwitchToClassicPlayerScoutButton';
import { ExploreLaunchButton } from '@/components/dataLocker/ExploreLandingPage/ExploreLaunchButton';
import { ExploreSavedFiltersText } from '@/components/dataLocker/ExploreLandingPage/ExploreSavedFiltersText';
import { PageTitle } from '@/components/head/PageTitle';
import { FilterSetTableBase } from '@/components/tables/FilterSetTable/FilterSetTable';
import { DATA_LOCKER_LANDING_PAGE_TAB } from '@/consts/searchKeys';
import { useExploreLandingPageTabs } from '@/hooks/explore/useExploreLandingPageTabs/useExploreLandingPageTabs';
import { useSetAtomFromParams } from '@/hooks/queryParams/useSetAtomFromParams';
import { useDeepEffect } from '@/hooks/useDeepEffect';
import { useFilterSets } from '@/hooks/useFilterSets';
import { useFilterSetType } from '@/hooks/useFilterSetType';
import { useNavigateToFilterSetId } from '@/hooks/useNavigateToFilterSetId';
import { usePlayerDataLockerApplyFiltersButton } from '@/hooks/usePlayerDataLockerApplyFiltersButton';
import { DataLockerSelectedTab } from '@/types/tabs';

const FilterSetTabContents = ({ tab }: { tab: DataLockerSelectedTab }) => {
  const { t } = useTranslation(['general']);
  const selectedTab = useAtomValue(dataLockerLandingPageSelectedTabAtom);
  const [selectedFilterIdToLoad, setSelectedFilterIdToLoad] = useState<string | null>(null);
  const filterSets = useFilterSets(useFilterSetType());

  const navigateToFilterSetId = useNavigateToFilterSetId(selectedTab);

  return (
    // If the selected tab is not the tab for this FilterSetContents, hide it. Note that the choice to render and hide
    // the tab is deliberate, as a fresh render would cause it to lose information about the previously selected sort order etc.
    <div className={tab !== selectedTab ? 'hidden' : ''}>
      <div className="flex pt-4 flex-1 flex-col" data-testid={`${tab}-tab-content`}>
        <div className="h-[322px]">
          <Suspense>
            <FilterSetTableBase
              selectedFilterIdToLoad={selectedFilterIdToLoad}
              onClick={value => setSelectedFilterIdToLoad(value)}
              tableWrapperClasses="!m-0 h-full"
              filterSets={filterSets}
            />
          </Suspense>
        </div>
        {filterSets.length > 0 && (
          <div className="pt-4 pl-4 flex justify-end">
            <Button disabled={!selectedFilterIdToLoad} onClick={() => navigateToFilterSetId(selectedFilterIdToLoad)}>
              {t('load')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const DataLockerLandingPage = () => {
  useSetAtomFromParams(
    dataLockerLandingPageSelectedTabAtom,
    DATA_LOCKER_LANDING_PAGE_TAB,
    Object.values(DataLockerSelectedTab),
    true,
  );
  const navigate = useNavigate();

  const { t } = useTranslation(['navigation']);
  const pageTitle = t('crossPlatform.primary.explore');
  const selectedTab = useAtomValue(dataLockerLandingPageSelectedTabAtom);
  const tabs = useExploreLandingPageTabs();

  const resetFilters = useSetAtom(resetAllFiltersAtom);
  const playerAggsFilterParams = useAtomValue(playerAggsFilterParamsAtom);

  const { onApply } = usePlayerDataLockerApplyFiltersButton();

  /**
   * I'm not exactly clear how this works - I was going to take this out,
   * but it seems that having this here is needed in order for the selected filters
   * reset if you go to a specific data locker, then go to the landing page, then select
   * a different data locker. I think this might also be needed to prevent the apply button
   * from showing when you go back to the landing page and then re-select the player data locker.
   *
   * Adding this here in case anyone else wonders about this!
   */
  useDeepEffect(() => {
    resetFilters();
    onApply();
  }, [playerAggsFilterParams]);

  return (
    <>
      <PageTitle>{pageTitle}</PageTitle>
      <div className="md:p-8 md:gap-8 flex justify-center items-center flex-1">
        <div className="w-full md:!w-3/4 xl:!w-1/2 h-full md:!h-auto">
          <Card>
            <CardHeaderWithTabs
              title={pageTitle}
              onTabChange={(_: MouseEvent, { value }: { value: DataLockerSelectedTab }) =>
                navigate(`?${DATA_LOCKER_LANDING_PAGE_TAB}=${value}`)
              }
              activeTab={selectedTab}
              tabs={tabs}
            >
              {selectedTab === DataLockerSelectedTab.PLAYER && <SwitchToClassicPlayerScoutButton />}
            </CardHeaderWithTabs>
            <CardBody>
              <div className="flex flex-col">
                <div className="py-4">
                  <div className="md:w-1/2">
                    <ExploreLaunchButton />
                  </div>
                  <div className="pt-8">
                    <ExploreSavedFiltersText />
                  </div>
                </div>
              </div>
              {/* One FilterSetTabContents for each tab, because we want to retain 
                  state separately on each of these tabs */}
              <FilterSetTabContents tab={DataLockerSelectedTab.PLAYER} />
              <FilterSetTabContents tab={DataLockerSelectedTab.TEAM} />
              <FilterSetTabContents tab={DataLockerSelectedTab.EVENTS} />
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
};
